import { Settings2Icon } from 'lucide-react';
import type { CompanyMembersQueryDTO } from '@shared/schemas/organization';
import { MembersToolbarButton } from '@/components/common/members-management';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

export type RoleFilter = CompanyMembersQueryDTO['role'];
export type StatusFilter = CompanyMembersQueryDTO['status'];
export type VerificationFilter = CompanyMembersQueryDTO['verification'];

interface CompanyMembersFilterSheetProps {
  open: boolean;
  role: RoleFilter;
  status: StatusFilter;
  verification: VerificationFilter;
  onOpenChange: (open: boolean) => void;
  onReset: () => void;
  onRoleChange: (value: RoleFilter) => void;
  onStatusChange: (value: StatusFilter) => void;
  onVerificationChange: (value: VerificationFilter) => void;
}

function FilterToggleGroup<Value extends string>({
  label,
  onChange,
  options,
  value,
}: {
  label: string;
  onChange: (value: Value) => void;
  options: ReadonlyArray<{ label: string; value: Value }>;
  value: Value;
}) {
  const labelId = `company-members-filter-${label.toLowerCase()}`;
  return (
    <Field>
      <FieldLabel id={labelId}>{label}</FieldLabel>
      <ToggleGroup
        aria-labelledby={labelId}
        className="flex flex-wrap justify-start"
        onValueChange={(nextValue) => {
          if (nextValue) onChange(nextValue as Value);
        }}
        type="single"
        value={value}
        variant="outline"
      >
        {options.map((option) => (
          <ToggleGroupItem key={option.value} value={option.value}>
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </Field>
  );
}

export function CompanyMembersFilterSheet(props: CompanyMembersFilterSheetProps) {
  return (
    <Sheet open={props.open} onOpenChange={props.onOpenChange}>
      <SheetTrigger asChild>
        <MembersToolbarButton className="shrink-0">
          Filtres
          <Settings2Icon data-icon="inline-end" />
        </MembersToolbarButton>
      </SheetTrigger>
      <SheetContent side="right" className="w-full border-border sm:max-w-md">
        <SheetHeader className="border-b border-border">
          <SheetTitle>Filtres</SheetTitle>
          <SheetDescription>
            Ajustez le rôle, le statut et la vérification sans quitter la table.
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-8 overflow-y-auto px-6 py-6">
          <FilterToggleGroup
            label="Rôle"
            onChange={props.onRoleChange}
            options={[
              { label: 'Tous', value: 'all' },
              { label: 'Administrateurs', value: 'admin' },
              { label: 'Membres', value: 'member' },
            ]}
            value={props.role}
          />
          <FilterToggleGroup
            label="Statut"
            onChange={props.onStatusChange}
            options={[
              { label: 'Tous', value: 'all' },
              { label: 'Actifs', value: 'active' },
              { label: 'Désactivés', value: 'disabled' },
            ]}
            value={props.status}
          />
          <FilterToggleGroup
            label="Vérification"
            onChange={props.onVerificationChange}
            options={[
              { label: 'Toutes', value: 'all' },
              { label: 'Vérifiés', value: 'verified' },
              { label: 'En attente', value: 'pending' },
            ]}
            value={props.verification}
          />
        </div>
        <SheetFooter className="border-t border-border sm:flex-row sm:justify-between">
          <Button variant="outline" onClick={props.onReset}>Réinitialiser</Button>
          <Button onClick={() => props.onOpenChange(false)}>Appliquer</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
